import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import { FaArrowLeft } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { billsAPI, restaurantAPI } from '../utils/api';
import './SalesAnalytics.css';

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Keep chart palette aligned with the Admin theme (reds/oranges + a few accents)
const COLORS = ['#d32f2f', '#ff5722', '#ff7043', '#ff9800', '#ffb300', '#42a5f5', '#ba68c8'];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const isoDayKey = (d) => {
  const x = startOfDay(d);
  // Use local day, not UTC, to match UI expectations
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDayLabel = (key) => {
  // key: YYYY-MM-DD
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
};

const weekStartKey = (d) => {
  // Monday-based week
  const x = startOfDay(d);
  const day = x.getDay(); // 0 Sun..6 Sat
  const diff = (day + 6) % 7; // Monday=0
  x.setDate(x.getDate() - diff);
  return isoDayKey(x);
};

const monthKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, 1);
  return dt.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const safeNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

function SalesAnalytics() {
  const [bills, setBills] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rangeDays, setRangeDays] = useState(90);
  const [pdfState, setPdfState] = useState({ loading: false, error: '' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const [restaurantRes] = await Promise.all([
          restaurantAPI.getCurrent(),
        ]);
        setRestaurant(restaurantRes?.data?.restaurant || null);

        // Load recent bills to power analytics.
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - rangeDays);

        const res = await billsAPI.getAll(from.toISOString(), to.toISOString());
        setBills(res.data?.bills || []);
      } catch (e) {
        setError(e.message || 'Failed to load sales analytics.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [rangeDays]);

  const metrics = useMemo(() => {
    const now = new Date();
    const todayKey = isoDayKey(now);
    const weekKey = weekStartKey(now);
    const monthK = monthKey(now);

    let total = 0;
    let todayTotal = 0;
    let thisWeekTotal = 0;
    let thisMonthTotal = 0;

    for (const bill of bills) {
      const created = bill.created_at ? new Date(bill.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;

      const t = safeNumber(bill.total);
      total += t;

      if (isoDayKey(created) === todayKey) todayTotal += t;
      if (weekStartKey(created) === weekKey) thisWeekTotal += t;
      if (monthKey(created) === monthK) thisMonthTotal += t;
    }

    return { total, todayTotal, thisWeekTotal, thisMonthTotal };
  }, [bills]);

  const dailySeries = useMemo(() => {
    // Last 14 days (or less if range is smaller)
    const days = Math.min(14, Math.max(7, rangeDays));

    const totalsByDay = new Map();
    for (const bill of bills) {
      const created = bill.created_at ? new Date(bill.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;
      const key = isoDayKey(created);
      totalsByDay.set(key, (totalsByDay.get(key) || 0) + safeNumber(bill.total));
    }

    const out = [];
    const cursor = startOfDay(new Date());
    cursor.setDate(cursor.getDate() - (days - 1));

    for (let i = 0; i < days; i += 1) {
      const key = isoDayKey(cursor);
      out.push({
        key,
        label: formatDayLabel(key),
        sales: +(totalsByDay.get(key) || 0).toFixed(2),
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return out;
  }, [bills, rangeDays]);

  const weeklySeries = useMemo(() => {
    // Last 8 weeks
    const totals = new Map();

    for (const bill of bills) {
      const created = bill.created_at ? new Date(bill.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;
      const key = weekStartKey(created);
      totals.set(key, (totals.get(key) || 0) + safeNumber(bill.total));
    }

    const out = [];
    const cursor = startOfDay(new Date());
    // move to this week start
    const wkStart = new Date(cursor);
    const d = wkStart.getDay();
    wkStart.setDate(wkStart.getDate() - ((d + 6) % 7));

    // go back 7 weeks
    wkStart.setDate(wkStart.getDate() - 7 * 7);

    for (let i = 0; i < 8; i += 1) {
      const key = isoDayKey(wkStart);
      const label = `${formatDayLabel(key)}`;
      out.push({ key, label, sales: +(totals.get(key) || 0).toFixed(2) });
      wkStart.setDate(wkStart.getDate() + 7);
    }

    return out;
  }, [bills]);

  const monthlySeries = useMemo(() => {
    // Last 6 months
    const totals = new Map();

    for (const bill of bills) {
      const created = bill.created_at ? new Date(bill.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;
      const key = monthKey(created);
      totals.set(key, (totals.get(key) || 0) + safeNumber(bill.total));
    }

    const out = [];
    const cursor = new Date();
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    cursor.setMonth(cursor.getMonth() - 5);

    for (let i = 0; i < 6; i += 1) {
      const key = monthKey(cursor);
      out.push({ key, label: formatMonthLabel(key), sales: +(totals.get(key) || 0).toFixed(2) });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return out;
  }, [bills]);

  const itemDistribution = useMemo(() => {
    // Pie chart: top items by revenue in selected range.
    const totals = new Map();

    for (const bill of bills) {
      for (const item of bill.items || []) {
        const name = item.name || 'Unknown';
        const lineTotal = safeNumber(item.lineTotal ?? item.total ?? 0);
        totals.set(name, (totals.get(name) || 0) + lineTotal);
      }
    }

    const rows = Array.from(totals.entries())
      .map(([name, value]) => ({ name, value: +value.toFixed(2) }))
      .sort((a, b) => b.value - a.value);

    const top = rows.slice(0, 6);
    const rest = rows.slice(6);
    const restSum = rest.reduce((acc, r) => acc + r.value, 0);

    return restSum > 0 ? [...top, { name: 'Others', value: +restSum.toFixed(2) }] : top;
  }, [bills]);

  const growth = useMemo(() => {
    // Growth rate visualized via daily line trend and a simple delta for the last 7 days vs previous 7 days.
    const totalsByDay = new Map();
    for (const bill of bills) {
      const created = bill.created_at ? new Date(bill.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;
      const key = isoDayKey(created);
      totalsByDay.set(key, (totalsByDay.get(key) || 0) + safeNumber(bill.total));
    }

    const sumRange = (startOffsetDays, len) => {
      const cursor = startOfDay(new Date());
      cursor.setDate(cursor.getDate() - startOffsetDays);
      let s = 0;
      for (let i = 0; i < len; i += 1) {
        const key = isoDayKey(cursor);
        s += totalsByDay.get(key) || 0;
        cursor.setDate(cursor.getDate() - 1);
      }
      return s;
    };

    const last7 = sumRange(0, 7);
    const prev7 = sumRange(7, 7);

    const change = last7 - prev7;
    const pct = prev7 > 0 ? (change / prev7) * 100 : null;

    return { last7, prev7, change, pct };
  }, [bills]);

  const downloadPdf = async () => {
    try {
      setPdfState({ loading: true, error: '' });

      const element = document.getElementById('sales-pdf-root');
      if (!element) throw new Error('PDF content not found');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Force single-page output: scale the snapshot to fit within one A4 page.
      const imgProps = pdf.getImageProperties(imgData);
      const rawW = imgProps.width;
      const rawH = imgProps.height;

      const scale = Math.min(pageWidth / rawW, pageHeight / rawH);
      const imgWidth = rawW * scale;
      const imgHeight = rawH * scale;

      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

      const safeName = (restaurant?.name || 'restaurant').replace(/[^a-z0-9-_]+/gi, '_');
      pdf.save(`${safeName}-sales-analytics.pdf`);
    } catch (e) {
      setPdfState({ loading: false, error: e.message || 'Failed to generate PDF' });
      return;
    }

    setPdfState({ loading: false, error: '' });
  };

  return (
    <div className="sales-analytics">
      <header className="sales-header">
        <Link to="/dashboard" className="back-btn">
          <FaArrowLeft /> Back
        </Link>
        <div className="sales-header-title">
          <h1>Sales Analytics</h1>
          <p>Daily, weekly and monthly performance overview</p>
        </div>
        <div className="sales-controls">
          <label className="sales-range">
            Data range:&nbsp;
            <select value={rangeDays} onChange={(e) => setRangeDays(parseInt(e.target.value, 10))}>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
          </label>
          <button
            type="button"
            className="back-btn"
            onClick={downloadPdf}
            disabled={pdfState.loading || loading}
            style={{ borderRadius: 16 }}
          >
            {pdfState.loading ? 'Preparing PDF…' : 'Download PDF'}
          </button>
        </div>
      </header>

      <div className="sales-content">
        {error && <div className="sales-error">{error}</div>}
        {pdfState.error && <div className="sales-error">{pdfState.error}</div>}

        {loading ? (
          <div className="sales-loading">Loading sales analytics…</div>
        ) : (
          <>
            {/* Hidden (offscreen) PDF layout. We capture this using html2canvas. */}
            <div
              id="sales-pdf-root"
              style={{
                position: 'absolute',
                left: '-10000px',
                top: 0,
                width: 800,
                background: '#fff',
                color: '#111827',
                padding: 20,
              }}
            >
              <div style={{ borderBottom: '2px solid #111827', paddingBottom: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{restaurant?.name || 'Restaurant'}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {restaurant?.location ? `Location: ${restaurant.location} • ` : ''}
                  {restaurant?.address ? `Address: ${restaurant.address} • ` : ''}
                  {restaurant?.phone ? `Phone: ${restaurant.phone} • ` : ''}
                  {restaurant?.email ? `Email: ${restaurant.email}` : ''}
                </div>
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  Sales Analytics Report • Range: Last {rangeDays} days • Generated: {new Date().toLocaleString()}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Today</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{INR.format(metrics.todayTotal)}</div>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>This Week</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{INR.format(metrics.thisWeekTotal)}</div>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>This Month</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{INR.format(metrics.thisMonthTotal)}</div>
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, minWidth: 220 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Selected Range Total</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{INR.format(metrics.total)}</div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Sales Trend (Daily)</div>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                      <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Day: ${l}`} />
                      <Line type="monotone" dataKey="sales" stroke="#d32f2f" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Sales by Week</div>
                  <div style={{ height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                        <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Week of ${l}`} />
                        <Bar dataKey="sales" fill="#ff7043" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Sales by Month</div>
                  <div style={{ height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={monthlySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                        <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Month: ${l}`} />
                        <Line type="monotone" dataKey="sales" stroke="#ff5722" strokeWidth={3} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Sales Distribution (Top Items)</div>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={itemDistribution} dataKey="value" nameKey="name" outerRadius={110} label>
                        {itemDistribution.map((entry, index) => (
                          <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => INR.format(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <section className="sales-metrics">
              <div className="metric-card">
                <div className="metric-label">Today</div>
                <div className="metric-value">{INR.format(metrics.todayTotal)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">This Week</div>
                <div className="metric-value">{INR.format(metrics.thisWeekTotal)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">This Month</div>
                <div className="metric-value">{INR.format(metrics.thisMonthTotal)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Selected Range Total</div>
                <div className="metric-value">{INR.format(metrics.total)}</div>
              </div>
            </section>

            <section className="sales-grid">
              <div className="chart-card">
                <h2>Sales Trend (Daily)</h2>
                <p className="chart-subtitle">Line graph shows whether sales are rising or falling day-by-day.</p>
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={dailySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                      <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Day: ${l}`} />
                      <Line type="monotone" dataKey="sales" stroke="#d32f2f" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="growth-summary">
                  <div>
                    <strong>Last 7 days:</strong> {INR.format(growth.last7)}
                  </div>
                  <div>
                    <strong>Previous 7 days:</strong> {INR.format(growth.prev7)}
                  </div>
                  <div>
                    <strong>Change:</strong> {INR.format(growth.change)}
                    {growth.pct == null ? '' : ` (${growth.pct.toFixed(1)}%)`}
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h2>Sales by Week</h2>
                <p className="chart-subtitle">Bar chart helps compare weekly performance at a glance.</p>
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={weeklySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                      <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Week of ${l}`} />
                      <Bar dataKey="sales" fill="#ff7043" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-card">
                <h2>Sales by Month</h2>
                <p className="chart-subtitle">Line graph highlights month-on-month growth and seasonality.</p>
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={monthlySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => INR.format(v).replace('₹', '')} />
                      <Tooltip formatter={(v) => INR.format(v)} labelFormatter={(l) => `Month: ${l}`} />
                      <Line type="monotone" dataKey="sales" stroke="#ff5722" strokeWidth={3} dot />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-card">
                <h2>Sales Distribution (Top Items)</h2>
                <p className="chart-subtitle">Pie chart shows which items contribute most to revenue.</p>
                <div className="chart-wrap">
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie data={itemDistribution} dataKey="value" nameKey="name" outerRadius={110} label>
                        {itemDistribution.map((entry, index) => (
                          <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => INR.format(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default SalesAnalytics;
